import { Loader2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/shared/lib/utils';

/**
 * Confirmation for a consequential action.
 *
 * `window.confirm` is never acceptable (§67): it cannot be styled, cannot
 * explain consequences, and blocks the whole browser.
 *
 * `children` renders between the description and the buttons, for an input
 * the confirmation needs (e.g. an audit reason).
 *
 * The `description` is where the real work happens — it should state what will
 * actually happen and to what, not ask "Are you sure?" (§68).
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  isPending = false,
  confirmDisabled = false,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  isPending?: boolean;
  /** Blocks confirming, e.g. while an impact preview loads or when it shows the change would be refused. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  children?: React.ReactNode;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-base">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-xs leading-relaxed">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {children}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} className="h-8 text-xs">
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              // The dialog closes on action by default; prevent that so it can
              // stay open showing a pending spinner until the mutation settles.
              event.preventDefault();
              onConfirm();
            }}
            disabled={isPending || confirmDisabled}
            className={cn(
              buttonVariants({ variant: variant === 'destructive' ? 'destructive' : 'default' }),
              'h-8 gap-1.5 text-xs',
            )}
          >
            {isPending && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
